/**
 * Helpers for detecting closed panels and reopening them in the flexlayout model.
 */

import { Actions, DockLocation, type Model, type TabNode } from "flexlayout-react"
import {
  PANEL_COMPONENTS,
  RESTORABLE_PANELS,
  type PanelComponent,
  EDITOR_TABSET_ID,
  CONSOLE_TABSET_ID,
  PDF_TABSET_ID,
  EDITOR_CONSOLE_SPLIT,
  LAYOUT_EDITOR_PLACEHOLDER_TAB_ID,
  LAYOUT_EDITOR_PLACEHOLDER_FILE_ID,
} from "./layout-model"
import { addEditorTabToTabset } from "./editor-tab"

type TabDef = {
  type: "tab"
  id?: string
  name: string
  component: PanelComponent
  config?: { fileId: string }
}

const PANEL_TAB_DEFS: Record<
  PanelComponent,
  {
    tabsetId: string
    tab: TabDef
    /** Preferred anchor component when the dedicated tabset was removed */
    anchorComponent: PanelComponent | null
    dockLocation: DockLocation
  }
> = {
  [PANEL_COMPONENTS.CODE_EDITOR]: {
    tabsetId: EDITOR_TABSET_ID,
    tab: {
      type: "tab",
      name: "untitled.tex",
      component: PANEL_COMPONENTS.CODE_EDITOR,
    },
    anchorComponent: null,
    dockLocation: DockLocation.CENTER,
  },
  [PANEL_COMPONENTS.CONSOLE]: {
    tabsetId: CONSOLE_TABSET_ID,
    tab: {
      type: "tab",
      id: "tab-console",
      name: "Console",
      component: PANEL_COMPONENTS.CONSOLE,
    },
    anchorComponent: PANEL_COMPONENTS.CODE_EDITOR,
    dockLocation: DockLocation.BOTTOM,
  },
  [PANEL_COMPONENTS.PDF_PREVIEW]: {
    tabsetId: PDF_TABSET_ID,
    tab: {
      type: "tab",
      id: "tab-pdf",
      name: "sample_file1.pdf",
      component: PANEL_COMPONENTS.PDF_PREVIEW,
    },
    anchorComponent: PANEL_COMPONENTS.CODE_EDITOR,
    dockLocation: DockLocation.RIGHT,
  },
  [PANEL_COMPONENTS.EPUB_PREVIEW]: {
    tabsetId: "epub-tabset",
    tab: {
      type: "tab",
      id: "tab-epub",
      name: "sample_file1.epub",
      component: PANEL_COMPONENTS.EPUB_PREVIEW,
    },
    anchorComponent: PANEL_COMPONENTS.PDF_PREVIEW,
    dockLocation: DockLocation.BOTTOM,
  },
  // IMAGE tabs are opened dynamically by the file explorer; no dedicated restore entry needed.
  [PANEL_COMPONENTS.IMAGE]: {
    tabsetId: EDITOR_TABSET_ID,
    tab: {
      type: "tab",
      name: "image",
      component: PANEL_COMPONENTS.IMAGE,
    },
    anchorComponent: PANEL_COMPONENTS.CODE_EDITOR,
    dockLocation: DockLocation.CENTER,
  },
}

function nodeExists(model: Model, nodeId: string): boolean {
  let found = false
  model.visitNodes((node) => {
    if (node.getId() === nodeId) found = true
  })
  return found
}

function getOpenComponents(model: Model): Set<PanelComponent> {
  const open = new Set<PanelComponent>()
  model.visitNodes((node) => {
    if (node.getType() === "tab") {
      const component = (node as TabNode).getComponent()
      if (component && RESTORABLE_PANELS.includes(component as PanelComponent)) {
        open.add(component as PanelComponent)
      }
    }
  })
  return open
}

export function getClosedPanels(model: Model): PanelComponent[] {
  const open = getOpenComponents(model)
  return RESTORABLE_PANELS.filter((panel) => !open.has(panel))
}

/** Tabset id used by the explorer open-file handler (must match default layout). */
export function getCodeEditorTabsetId(model: Model): string | null {
  if (nodeExists(model, EDITOR_TABSET_ID)) return EDITOR_TABSET_ID

  const byComponent = findTabsetForComponent(model, PANEL_COMPONENTS.CODE_EDITOR)
  if (
    byComponent &&
    byComponent !== CONSOLE_TABSET_ID &&
    byComponent !== PDF_TABSET_ID
  ) {
    return byComponent
  }
  return null
}

function isEditorLikeTab(tab: TabNode): boolean {
  const component = tab.getComponent()
  return (
    component === PANEL_COMPONENTS.CODE_EDITOR ||
    component === PANEL_COMPONENTS.IMAGE
  )
}

function isLayoutPlaceholderTab(tab: TabNode): boolean {
  const cfg = tab.getConfig() as { fileId?: string } | undefined
  return cfg?.fileId === LAYOUT_EDITOR_PLACEHOLDER_FILE_ID
}

/** Remove the structural placeholder once a real editor file tab exists. */
export function removeLayoutPlaceholderTabs(model: Model) {
  let hasRealEditorTab = false
  model.visitNodes((node) => {
    if (node.getType() !== "tab") return
    const tab = node as TabNode
    if (!isEditorLikeTab(tab) || isLayoutPlaceholderTab(tab)) return
    if (tab.getParent()?.getId() === EDITOR_TABSET_ID) {
      hasRealEditorTab = true
    }
  })
  if (!hasRealEditorTab) return

  const toDelete: string[] = []
  model.visitNodes((node) => {
    if (node.getType() !== "tab") return
    const tab = node as TabNode
    if (isLayoutPlaceholderTab(tab)) toDelete.push(tab.getId())
  })
  for (const tabId of toDelete) {
    model.doAction(Actions.deleteTab(tabId))
  }
}

function ensureEditorTabsetWithPlaceholder(model: Model) {
  if (nodeExists(model, EDITOR_TABSET_ID)) return

  if (!nodeExists(model, CONSOLE_TABSET_ID)) return

  model.doAction(
    Actions.addNode(
      { type: "tabset", id: EDITOR_TABSET_ID, weight: EDITOR_CONSOLE_SPLIT.editor },
      CONSOLE_TABSET_ID,
      DockLocation.TOP,
      -1,
      false,
    ),
  )
  if (!nodeExists(model, EDITOR_TABSET_ID)) return

  model.doAction(
    Actions.addNode(
      {
        type: "tab",
        id: LAYOUT_EDITOR_PLACEHOLDER_TAB_ID,
        name: "\u00a0",
        component: PANEL_COMPONENTS.CODE_EDITOR,
        config: { fileId: LAYOUT_EDITOR_PLACEHOLDER_FILE_ID },
      },
      EDITOR_TABSET_ID,
      DockLocation.CENTER,
      -1,
      false,
    ),
  )
}

function ensureEditorTabsetHasPlaceholder(model: Model) {
  if (!nodeExists(model, EDITOR_TABSET_ID)) return

  let tabCount = 0
  model.visitNodes((node) => {
    if (node.getType() !== "tab") return
    const tab = node as TabNode
    if (tab.getParent()?.getId() !== EDITOR_TABSET_ID) return
    tabCount += 1
  })

  if (tabCount === 0) {
    model.doAction(
      Actions.addNode(
        {
          type: "tab",
          id: LAYOUT_EDITOR_PLACEHOLDER_TAB_ID,
          name: "\u00a0",
          component: PANEL_COMPONENTS.CODE_EDITOR,
          config: { fileId: LAYOUT_EDITOR_PLACEHOLDER_FILE_ID },
        },
        EDITOR_TABSET_ID,
        DockLocation.CENTER,
        -1,
        false,
      ),
    )
  }
}

/**
 * Keep editor files in the editor tabset and console in its own tabset below.
 * flexlayout removes empty tabsets, which previously caused main.tex to open in Console.
 */
export function normalizeEditorConsoleLayout(model: Model) {
  const misplacedEditorTabIds: string[] = []
  model.visitNodes((node) => {
    if (node.getType() !== "tab") return
    const tab = node as TabNode
    if (!isEditorLikeTab(tab) || isLayoutPlaceholderTab(tab)) return
    const parentId = tab.getParent()?.getId()
    if (parentId !== EDITOR_TABSET_ID) {
      misplacedEditorTabIds.push(tab.getId())
    }
  })

  ensureEditorTabsetWithPlaceholder(model)

  if (nodeExists(model, EDITOR_TABSET_ID)) {
    for (const tabId of misplacedEditorTabIds) {
      model.doAction(
        Actions.moveNode(tabId, EDITOR_TABSET_ID, DockLocation.CENTER, -1, false),
      )
    }
    removeLayoutPlaceholderTabs(model)
    ensureEditorTabsetHasPlaceholder(model)
  }
}

function findTabsetForComponent(
  model: Model,
  component: PanelComponent,
): string | null {
  let tabsetId: string | null = null
  model.visitNodes((node) => {
    if (node.getType() === "tab") {
      const tab = node as TabNode
      if (tab.getComponent() === component) {
        const parent = tab.getParent()
        if (parent?.getType() === "tabset") {
          tabsetId = parent.getId()
        }
      }
    }
  })
  return tabsetId
}

function selectFirstTabWithComponent(model: Model, component: PanelComponent) {
  model.visitNodes((node) => {
    if (node.getType() === "tab") {
      const tab = node as TabNode
      if (tab.getComponent() === component) {
        model.doAction(Actions.selectTab(tab.getId()))
      }
    }
  })
}

/** Remove tabs that are not a working code-editor (e.g. "[unnamed tab]" placeholders). */
function removeBrokenEditorTabs(model: Model, tabsetId: string) {
  const toDelete: string[] = []
  model.visitNodes((node) => {
    if (node.getType() !== "tab") return
    const tab = node as TabNode
    if (tab.getParent()?.getId() !== tabsetId) return
    if (tab.getComponent() !== PANEL_COMPONENTS.CODE_EDITOR) {
      toDelete.push(tab.getId())
    }
  })
  for (const tabId of toDelete) {
    model.doAction(Actions.deleteTab(tabId))
  }
}

/** Resolve tabset id for "+"-style editor tab (matches working tab bar target).
 *  Creates a new tabset if none exists — safe to call when the editor starts empty. */
export function ensureEditorTabsetId(model: Model): string {
  normalizeEditorConsoleLayout(model)
  return getCodeEditorTabsetId(model) ?? EDITOR_TABSET_ID
}

/** Same behavior as the tab bar "+" button. */
function reopenCodeEditorPanel(model: Model) {
  const tabsetId = ensureEditorTabsetId(model)
  removeBrokenEditorTabs(model, tabsetId)
  addEditorTabToTabset(model, tabsetId)
}

/** Reopen a closed panel (no-op if already open — focuses it instead). */
export function reopenPanel(model: Model, component: PanelComponent) {
  const open = getOpenComponents(model)
  if (open.has(component)) {
    selectFirstTabWithComponent(model, component)
    return
  }

  const def = PANEL_TAB_DEFS[component]

  if (component === PANEL_COMPONENTS.CODE_EDITOR) {
    reopenCodeEditorPanel(model)
    return
  }

  if (nodeExists(model, def.tabsetId)) {
    model.doAction(
      Actions.addNode(def.tab, def.tabsetId, DockLocation.CENTER, -1, true),
    )
    return
  }

  if (def.anchorComponent) {
    const anchorTabsetId = findTabsetForComponent(model, def.anchorComponent)
    if (anchorTabsetId) {
      model.doAction(
        Actions.addNode(
          def.tab,
          anchorTabsetId,
          def.dockLocation,
          -1,
          true,
        ),
      )
      return
    }
  }

  const rootId = model.getRoot().getId()
  model.doAction(
    Actions.addNode(def.tab, rootId, DockLocation.CENTER, -1, true),
  )
}
